import { useCallback, useMemo } from 'react';
import { useAppStore, setAppStore } from '@/store';
import { useAuth } from '@/hooks/useAuth';
import { resetCanvasLoadState } from '@/utils/canvas/canvasLoadState';
import { openRegistrationModal } from '@/utils/user/openRegistrationModal';
import { sendAiGenScreenActionAnalytics } from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import { getScreenType } from '@/utils/analytics/getScreenType';
import { AiGenScreenAction } from '@/types/analytics';
import { closeMiniapp } from '@/utils/editor/closeAndNavigate';
import { extractUrlParams } from '@/utils/ui/extractUrlParams';
import { DeeplinkKeys } from '@/consts/deepLinks';

import type { IAppStore } from '@/store';

// External selector for stable reference
const selector = (store: IAppStore) => ({
  screen: store.screen,
  setScreen: store.setScreen,
  previousScreen: store.previousScreen,
});

/**
 * Format timestamp as "Jan 25, 2:30 PM"
 */
export const formatTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Hook for Header component state and handlers.
 */
export const useHeaderState = () => {
  const { screen, setScreen, previousScreen } = useAppStore(selector);
  const { isAuthorized } = useAuth();

  // Read hideBackButton deeplink param once (stable across re-renders)
  const hideBackButton = useMemo(() => {
    const params = extractUrlParams([DeeplinkKeys.hideBackButton]);
    return params[DeeplinkKeys.hideBackButton] === 'true';
  }, []);

  // Back button shown on mobile only (CSS handles hiding on desktop)
  // Hide on library screen, and on create screen when hideBackButton deeplink param is true
  const showBackButton =
    screen !== 'library' && !(screen === 'create' && hideBackButton);

  // Generations button shown on mobile only (CSS handles hiding on desktop)
  // Only show for library and create screens
  const showGenerationsButton = screen === 'library' || screen === 'create';

  const handleBack = useCallback(() => {
    // Send BACK analytics
    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.BACK,
      screen_type: getScreenType(),
    });

    if (screen === 'create') {
      // Don't reset - preserve uploaded media for when user selects another preset
      // Reset canvas load state so images can be refreshed on next preset selection
      resetCanvasLoadState();
      setAppStore({ shouldScrollToActiveTab: true });
      setScreen('library');
    } else if (screen === 'files') {
      // Go back to previous screen if available, otherwise library
      if (previousScreen && previousScreen !== 'files') {
        setScreen(previousScreen);
      } else {
        setAppStore({ shouldScrollToActiveTab: true });
        setScreen('library');
      }
    }
  }, [screen, setScreen, previousScreen]);

  const handleGenerationsClick = useCallback(() => {
    setScreen('files');
  }, [setScreen]);

  const handleCloseApp = useCallback(() => {
    // Send CANCEL analytics
    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.CANCEL,
      screen_type: getScreenType(),
    });

    try {
      closeMiniapp();
    } catch {
      // Failed to close miniapp - SDK may not be available
    }
  }, []);

  const handleLogin = useCallback(() => {
    openRegistrationModal();
  }, []);

  /**
   * Get title based on screen (for mobile)
   */
  const getMobileTitle = useCallback(() => {
    switch (screen) {
      case 'library':
        return 'GenAI Studio';
      case 'create':
        return 'Select WoW Presets';
      case 'loading':
        return 'Generating...';
      case 'files':
        return 'Your Creations';
      default:
        return 'GenAI Studio';
    }
  }, [screen]);

  return {
    screen,
    isAuthorized,
    showBackButton,
    showGenerationsButton,
    handleBack,
    handleGenerationsClick,
    handleCloseApp,
    handleLogin,
    getMobileTitle,
  };
};
